/**
 * Thin axios wrapper for the ZMEX Customer Portal API.
 *
 * Contract:
 *   - Always send credentials (session cookie) with every request.
 *   - Automatically attach the CSRF token (double-submit cookie) on
 *     state-changing verbs.
 *   - Normalize error shape to `{ status, detail }` on rejection.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const CSRF_COOKIE_NAME = 'zmex_csrf';

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const rows = document.cookie ? document.cookie.split(';') : [];
  for (const row of rows) {
    const [k, ...rest] = row.trim().split('=');
    if (k === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status ?? 0;
    const detail =
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.message ||
      'Unexpected error.';

    // CSRF self-heal: if a state-changing request fails with 403 "CSRF
    // check failed" (missing/stale cookie), hit /auth/me once (which
    // re-issues the zmex_csrf cookie) and retry the original request
    // with the freshly-read cookie value. Guarded by _csrfRetry so a
    // still-failing request can't loop.
    const original = err?.config;
    const isCsrfFailure =
      status === 403 &&
      typeof detail === 'string' &&
      detail.toLowerCase().includes('csrf');
    if (isCsrfFailure && original && !original._csrfRetry) {
      original._csrfRetry = true;
      try {
        await api.get('/auth/me');
        const fresh = readCookie(CSRF_COOKIE_NAME);
        if (fresh) {
          original.headers = original.headers || {};
          original.headers['X-CSRF-Token'] = fresh;
        }
        return api.request(original);
      } catch {
        /* fall through to reject below */
      }
    }
    return Promise.reject({ status, detail, raw: err });
  },
);

// -------------------------------------------------------------------------
// Convenience endpoints
// -------------------------------------------------------------------------
export const authApi = {
  requestCode: (contact) => api.post('/auth/request-code', { contact }),
  verifyCode: (pending_id, code) =>
    api.post('/auth/verify-code', { pending_id, code }),
  resendCode: (pending_id) => api.post('/auth/resend-code', { pending_id }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

export const portalApi = {
  dashboard: () => api.get('/portal/dashboard'),
  accounts: () => api.get('/portal/accounts'),
  accountDashboard: (accountId) =>
    api.get(`/portal/accounts/${accountId}/dashboard`),
  accountInvoices: (accountId) =>
    api.get(`/portal/accounts/${accountId}/invoices`),
  accountPayments: (accountId) =>
    api.get(`/portal/accounts/${accountId}/payments`),
  updateContactInfo: (accountId, fields) =>
    api.patch(`/portal/accounts/${accountId}/contact-info`, fields),
};

export const adminApi = {
  login: (adminToken) => api.post('/admin/login', { admin_token: adminToken }),
  logout: () => api.post('/admin/logout'),
  sessionStatus: () => api.get('/admin/session'),
  customers: (opts) =>
    api.get('/admin/customers', {
      params: opts?.includeRemoved ? { include_removed: 1 } : {},
      // Hub sync fans out to N /account-profile calls; give it plenty of
      // headroom so a slow Hub doesn't leave the admin list stale.
      timeout: 60_000,
    }),
  impersonateById: (portalCustomerId, reason) =>
    api.post('/admin/impersonate', {
      portal_customer_id: portalCustomerId,
      reason: reason || null,
    }),
  impersonateByContact: (contact, reason) =>
    api.post('/admin/impersonate', { contact, reason: reason || null }),
  exit: () => api.post('/admin/impersonate/exit'),
  heartbeat: () => api.post('/admin/impersonate/heartbeat'),
  chargeAutopay: (accountId, opts) =>
    api.post(`/admin/accounts/${accountId}/autopay/charge`, {
      invoice_ids: opts?.invoiceIds || null,
      reason: opts?.reason || null,
    }),
  zohoSearch: (q) => api.get('/admin/zoho/search', { params: { q } }),
  linkAccount: (portalCustomerId, zohoCustomerId, reason) =>
    api.post(`/admin/customers/${portalCustomerId}/link-account`, {
      zoho_customer_id: zohoCustomerId,
      reason: reason || null,
    }),
  listLinkedAccounts: (portalCustomerId) =>
    api.get(`/admin/customers/${portalCustomerId}/accounts`),
  unlinkAccount: (portalCustomerId, accountId) =>
    api.delete(`/admin/customers/${portalCustomerId}/accounts/${accountId}`),
  getServiceInfo: (accountId) =>
    api.get(`/admin/accounts/${accountId}/service-info`),
  patchServiceInfo: (accountId, fields) =>
    api.patch(`/admin/accounts/${accountId}/service-info`, fields),
  resetServiceInfo: (accountId) =>
    api.post(`/admin/accounts/${accountId}/service-info/reset`),
  diagnoseLogin: (contact) =>
    api.post('/admin/diagnose-login', { contact }),
  getAnnouncement: () => api.get('/admin/announcement'),
  saveAnnouncement: (textEn, textEs) =>
    api.put('/admin/announcement', { text_en: textEn, text_es: textEs }),
  bulkApplyServiceInfo: (accountIds, fields) =>
    api.post('/admin/service-info/bulk-apply', { account_ids: accountIds, fields }),
  removeCustomer: (portalCustomerId) =>
    api.post(`/admin/customers/${portalCustomerId}/remove`),
  restoreCustomer: (portalCustomerId) =>
    api.post(`/admin/customers/${portalCustomerId}/restore`),
};

export const paymentApi = {
  createPayIntent: (accountId, invoiceId) =>
    api.post(`/portal/accounts/${accountId}/pay/intent`, { invoice_id: invoiceId }),
  payStatus: (accountId, paymentIntentId) =>
    api.get(`/portal/accounts/${accountId}/pay/status/${paymentIntentId}`),
  trackPayEvent: (accountId, payload) =>
    api.post(`/portal/accounts/${accountId}/pay/event`, payload),
  createSetupIntent: (accountId) =>
    api.post(`/portal/accounts/${accountId}/autopay/setup-intent`),
  listPaymentMethods: (accountId) =>
    api.get(`/portal/accounts/${accountId}/payment-methods`),
  setAutopayDefault: (accountId, paymentMethodId) =>
    api.post(`/portal/accounts/${accountId}/autopay/set-default`, {
      payment_method_id: paymentMethodId,
    }),
  disableAutopay: (accountId) =>
    api.post(`/portal/accounts/${accountId}/autopay/disable`),
  detachPaymentMethod: (accountId, paymentMethodId) =>
    api.delete(`/portal/accounts/${accountId}/payment-methods/${paymentMethodId}`),
};

export const supportApi = {
  create: (formData) =>
    api.post('/portal/support/requests', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  list: () => api.get('/portal/support/requests'),
  get: (requestId) => api.get(`/portal/support/requests/${requestId}`),
  liveStatus: (requestId) =>
    api.get(`/portal/support/requests/${requestId}/live-status`),
  addUpdate: (requestId, formData) =>
    api.post(`/portal/support/requests/${requestId}/updates`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  attachmentUrl: (attachmentId) =>
    `${API_BASE}/portal/support/attachments/${attachmentId}`,
};
